/**
 * The real transport for the calendar reads. ONE `fetch`, and it is deliberately the same
 * `fetch` the rest of the cabinet makes.
 *
 * BEARER ONLY, NO COOKIES, CROSS-ORIGIN — adr/0002 puts the cabinet on `authors.moonx.app` and the
 * API on `moonx.art/authors-api`, different origins, so `credentials` stays at its default and the
 * token travels in the header. That reasoning is `features/cabinet/api/http-cabinet-api.ts`
 * and it is quoted rather than re-derived: if it is ever wrong there it is wrong here, and one
 * wrong place is better than two that disagree.
 *
 * IT RUNS ONLY IN A BUILD THAT SETS `VITE_AUTHORS_API_LIVE=true` (`dev:stack` does); every other
 * build gets the fixture from ./calendar-api-source. It was written next to the fixture for the
 * reason the cabinet's own header gives: a transport written later against screens that grew up
 * on fixtures is a transport that discovers six shape mismatches on its first day.
 *
 * ★ EVERY GET DECLARES `Cache-Control: no-store` ON ITS 200 (`authors-api.yaml`,
 * `:2064-2066`) and there is no CDN in front (adr/0002), so the browser cache is the only cache
 * there is. `cache: 'no-store'` on the way out means a stale calendar cannot come back off disk if
 * a proxy ever drops the header.
 */
import { AUTHORS_API_BASE_URL } from '../../../config';
import { i18n } from '../../../i18n';
import { sendWithSession } from '../../auth/session/send-with-session';
import { ApiProblem, NotFoundFailure, TransportFailure, retryAfterOf } from '../../cabinet/api';
import type { OperationQuery, Problem } from '../../cabinet/api';
import type {
  AuthorCalendar,
  CalendarApi,
  CalendarGroupList,
  PublishDateSuggestions,
} from './calendar-api';

/** How the app gets a Firebase ID token. Sign-in is a different surface and this feature must not
 *  grow a second Firebase client, so the session owner hands one function in. */
export type AccessTokenSource = (forceRefresh?: boolean) => Promise<string | null>;

export interface HttpCalendarApiOptions {
  readonly baseUrl?: string;
  readonly getAccessToken: AccessTokenSource;
}

type QueryValue = string | number | boolean | undefined | null;

function toSearch(query: Readonly<Record<string, QueryValue>> | undefined): string {
  if (query === undefined) {
    return '';
  }
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) {
      continue;
    }
    params.append(key, String(value));
  }
  const search = params.toString();
  return search.length > 0 ? `?${search}` : '';
}

function isProblem(value: unknown): value is Problem {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as Partial<Problem>;
  return typeof candidate.error_code === 'string' && typeof candidate.status === 'number';
}

export function createHttpCalendarApi(options: HttpCalendarApiOptions): CalendarApi {
  const baseUrl = options.baseUrl ?? AUTHORS_API_BASE_URL;

  async function request(
    path: string,
    query: Readonly<Record<string, QueryValue>> | undefined,
    signal: AbortSignal | undefined,
  ): Promise<unknown> {
    // The 401 rule is the shared one, like the cabinet's own client: one forced refresh and one
    // retry on a bare 401, and a refused credential ends the session
    // (features/auth/session/send-with-session.ts). `forceRefresh` is why the token type above
    // takes an argument.
    const response = await sendWithSession(options.getAccessToken, async (token) => {
      const headers = new Headers({ Accept: 'application/json, application/problem+json' });
      if (token !== null) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      try {
        return await fetch(`${baseUrl}${path}${toSearch(query)}`, {
          method: 'GET',
          headers,
          cache: 'no-store',
          ...(signal === undefined ? {} : { signal }),
        });
      } catch (cause) {
        // An AbortError is the caller's own doing and must stay one, so `useAsync` ignores it
        // rather than painting an error over a screen the reader has already left.
        if (cause instanceof DOMException && cause.name === 'AbortError') {
          throw cause;
        }
        throw new TransportFailure(
          'The request did not reach the platform.',
          cause === undefined ? undefined : { cause },
        );
      }
    });

    if (!response.ok) {
      const retryAfter = retryAfterOf(response.headers);
      let payload: unknown;
      try {
        payload = await response.json();
      } catch {
        payload = undefined;
      }
      if (isProblem(payload)) {
        throw new ApiProblem(payload, retryAfter);
      }
      if (response.status === 404) {
        // No calendar read DECLARES a 404 (`authors-api.yaml`, `:2066-2069`), so this is
        // a routing or deployment fault rather than a modelled outcome. It is still caught, because
        // an undeclared status reaching a screen as "undefined" is worse than one named plainly.
        throw new NotFoundFailure('The calendar is not being served at that address.', 'calendar');
      }
      throw new TransportFailure(`The platform answered ${String(response.status)}.`);
    }

    return await response.json();
  }

  return {
    getAuthorCalendar: (query: OperationQuery<'getAuthorCalendar'>, signal) =>
      request(
        '/v1/calendar',
        query as unknown as Readonly<Record<string, QueryValue>>,
        signal,
      ) as Promise<AuthorCalendar>,

    getPublishDateSuggestions: (query, signal) =>
      request(
        '/v1/calendar/suggestions',
        query as unknown as Readonly<Record<string, QueryValue>> | undefined,
        signal,
      ) as Promise<PublishDateSuggestions>,

    listCalendarGroups: (signal) =>
      request('/v1/calendar/groups', { lang: i18n.language }, signal) as Promise<CalendarGroupList>,
  };
}
